import { MonitorPlay } from 'lucide-react'
import { useState } from 'react'
import { FiFilter, FiPlus } from 'react-icons/fi'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { YOUTUBE_CHANNEL_URL } from '@domain/features/youtube/youtube-content'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import HackCard from '@presentation/components/ui/HackCard'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { usePdHackCategories } from '@presentation/hooks/usePdHackCategories'
import { useHacks } from '@presentation/hooks/useHacks'
import { useScrollToTopOnRouteEnter } from '@presentation/hooks/useScrollToTopOnRouteEnter'
import SeoHead from '@presentation/seo/SeoHead'

export default function HacksListPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const isHacksListRoute = location.pathname === '/hacks'
  const screenPath = `${location.pathname}${location.search || ''}`
  useScrollToTopOnRouteEnter({ enabled: isHacksListRoute })
  const { user } = useAuthUser()
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [draftCategoryId, setDraftCategoryId] = useState<string | null>(null)
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = usePdHackCategories(true, isFilterOpen)
  const {
    hacks,
    loading,
    error,
  } = useHacks(categoryId)

  const openFilterPopup = () => {
    setDraftCategoryId(categoryId)
    setIsFilterOpen(true)
  }

  const closeFilterPopup = () => {
    setIsFilterOpen(false)
  }

  const applyFilterSelection = () => {
    if (draftCategoryId) {
      AnalyticsFeature.trackFilterApply(screenPath, 'hacks_filter', draftCategoryId, Boolean(user))
    } else {
      AnalyticsFeature.trackFilterClear(screenPath, 'hacks_filter', Boolean(user))
    }
    setCategoryId(draftCategoryId)
    setIsFilterOpen(false)
  }

  const removeFilters = () => {
    AnalyticsFeature.trackFilterClear(screenPath, 'hacks_filter', Boolean(user))
    setDraftCategoryId(null)
    setCategoryId(null)
    setIsFilterOpen(false)
  }

  const navigateBackToHome = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate('/', { replace: true })
  }

  return (
    <div className="bg-background pb-8 pt-3 sm:pb-12 sm:pt-4">
      {isHacksListRoute ? (
        <SeoHead
          title="Michel's PD Hacks | Parkinson's Insights"
          description="Discover practical Parkinson's hacks, symptom-focused tips, and community-shared solutions."
          canonicalPath="/hacks"
        />
      ) : null}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={navigateBackToHome}
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light md:hidden"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>

        <div className="mt-5 ml-1 flex items-center justify-between gap-3 pl-1 sm:mt-6 sm:ml-2 sm:pl-2">
          <h1 className="text-3xl font-bold tracking-tight text-black sm:text-4xl">
            Michel&apos;s PD Hacks
          </h1>
          <div className="flex items-start gap-2 sm:items-center">
            {user?.isAdmin ? (
              <Link
                to="/hacks/create"
                aria-label="Create hack"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-blue bg-surface text-blue transition-colors hover:bg-blue/10"
              >
                <FiPlus className="h-5 w-5" aria-hidden="true" />
              </Link>
            ) : null}
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={openFilterPopup}
                className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors ${
                  categoryId
                    ? 'border-blue bg-blue text-white hover:bg-blue/85'
                    : 'border-blue text-blue hover:bg-blue/8'
                }`}
              >
                <FiFilter className="h-4 w-4" aria-hidden="true" />
                Filter
              </button>
              <a
                href={YOUTUBE_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  AnalyticsFeature.trackExternalLinkOpen(
                    screenPath,
                    'hacks_list',
                    'external_link',
                    YOUTUBE_CHANNEL_URL,
                    Boolean(user),
                  )
                }}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-blue px-4 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition-colors hover:bg-blue/90"
              >
                <MonitorPlay className="h-4 w-4" aria-hidden="true" />
                See on YouTube
              </a>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner className="mt-8" />
        ) : error ? (
          <p className="mt-8 text-center text-text-secondary">
            Unable to load Michel&apos;s hacks. Please try again later.
          </p>
        ) : hacks.length === 0 ? (
          <div className="mt-12 text-center">
            <p className="text-lg font-semibold text-text-secondary">
              No hacks available yet.
            </p>
            {categoryId ? (
              <p className="mt-1 text-sm text-text-muted">
                No hacks in this category. Try selecting a different one.
              </p>
            ) : null}
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {hacks.map((hack) => (
              <HackCard key={hack.uid} hack={hack} />
            ))}
          </div>
        )}
      </div>

      {isFilterOpen && (
        <div className="fixed inset-0 z-60 bg-black/55 px-4 py-8 sm:px-6">
          <button
            type="button"
            className="absolute inset-0"
            aria-label="Close filters"
            onClick={closeFilterPopup}
          />

          <section
            role="dialog"
            aria-modal="true"
            aria-label="Hack filters"
            className="relative z-10 mx-auto flex w-full max-w-xl flex-col rounded-2xl bg-surface p-5 shadow-2xl sm:p-6"
          >
            <h3 className="text-2xl font-bold text-text-primary">Filter hacks</h3>
            <p className="mt-1 text-base text-text-secondary">
              Select one category and apply when ready.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setDraftCategoryId(null)}
                className={`rounded-full px-5 py-2.5 text-base font-semibold transition-colors ${
                  !draftCategoryId
                    ? 'bg-blue text-white'
                    : 'bg-surface text-black shadow-sm hover:bg-border-light'
                }`}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category.uid}
                  type="button"
                  onClick={() => {
                    setDraftCategoryId((current) => (
                      current === category.uid ? null : category.uid
                    ))
                  }}
                  className={`rounded-full px-5 py-2.5 text-base font-semibold transition-colors ${
                    draftCategoryId === category.uid
                      ? 'bg-blue text-white'
                      : 'bg-surface text-black shadow-sm hover:bg-border-light'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {categoriesLoading ? (
              <p className="mt-3 text-sm font-semibold text-text-secondary">
                Loading categories...
              </p>
            ) : categoriesError ? (
              <p className="mt-3 text-sm font-semibold text-text-secondary">
                Unable to load categories. Please try again.
              </p>
            ) : categories.length === 0 ? (
              <p className="mt-3 text-sm font-semibold text-text-secondary">
                No categories available.
              </p>
            ) : null}

            <div className="mt-6 flex flex-col gap-2">
              <button
                type="button"
                onClick={applyFilterSelection}
                disabled={categoriesLoading}
                className="w-full rounded-lg bg-violet px-5 py-2.5 text-base font-semibold text-white transition-opacity hover:opacity-90"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={closeFilterPopup}
                className="w-full px-2 py-2 text-base font-semibold text-black transition-colors hover:text-primary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={removeFilters}
                className="w-full px-2 py-2 text-base font-semibold text-black transition-colors hover:text-primary"
              >
                Remove filters
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
